import React from "react";
import WordSvg from "../../../assets/Word.svg";
import PdfSvg from "../../../assets/PDF.svg";
import ExcelSvg from "../../../assets/Excel.svg";

/** Icons render at tooltip-header size unless the caller asks for the chip size. */
export interface DocumentIconProps {
  size?: number;
}

const DEFAULT_ICON_SIZE = 24;

const iconStyle = (size: number): React.CSSProperties => ({
  width: size,
  height: size,
  flexShrink: 0,
});

// The build's SVG optimiser drops a viewBox that equals the file's width and
// height, and without one the drawing keeps its native size instead of
// scaling to the box it is given, so a 14px chip shows a cropped 24px logo.
// The boxes are restated here from the asset files, so any size renders whole.
const WORD_VIEW_BOX = "0 0 24 23";
const PDF_VIEW_BOX = "0 0 20 23";
const EXCEL_VIEW_BOX = "0 0 24 23";

export const WordIcon: React.FC<DocumentIconProps> = ({ size = DEFAULT_ICON_SIZE }) => (
  <WordSvg viewBox={WORD_VIEW_BOX} style={iconStyle(size)} />
);

export const PdfIcon: React.FC<DocumentIconProps> = ({ size = DEFAULT_ICON_SIZE }) => (
  <PdfSvg viewBox={PDF_VIEW_BOX} style={iconStyle(size)} />
);

export const ExcelIcon: React.FC<DocumentIconProps> = ({ size = DEFAULT_ICON_SIZE }) => (
  <ExcelSvg viewBox={EXCEL_VIEW_BOX} style={iconStyle(size)} />
);

/**
 * Generic file glyph, used for any file type without a brand icon.
 */
export const FileIcon: React.FC<DocumentIconProps> = ({ size = DEFAULT_ICON_SIZE }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: "#9E9E9E", flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/**
 * File-type icon for a document title. A type without a brand icon gets the
 * generic glyph, so an unknown extension is never labelled as a Word file.
 */
export const getDocumentTheme = (title: string = ""): { Icon: React.FC<DocumentIconProps> } => {
  const name = title.toLowerCase();

  if (name.endsWith(".pdf")) {
    return { Icon: PdfIcon };
  }
  if (name.endsWith(".docx")) {
    return { Icon: WordIcon };
  }
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    return { Icon: ExcelIcon };
  }

  return { Icon: FileIcon };
};

/**
 * Book icon for playbook citations (chip + tooltip header).
 * Size defaults to 24 (tooltip header); pass size=12 for the inline chip.
 */
export const PlaybookIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = "#5C6BC0",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

/**
 * Magnifying-glass glyph for catylex citations — mirrors the SearchIcon
 * shown in the CatylexSearchSection header so the citation chip and the
 * inline search result it came from share a visual identity. Stroked in
 * the catylex brand blue (#106ebe).
 */
export const CatylexIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = "#106ebe",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
